import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  TablePagination,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useAuth } from '@/context/AuthContext';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { Dayjs } from 'dayjs';
import { format } from 'date-fns';
import { apiService } from '@/services/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
  Font,
} from '@react-pdf/renderer';

try {
  Font.register({
    family: 'Roboto',
    fonts: [
      {
        src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
      },
      {
        src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
        fontWeight: 700,
      },
    ],
  });
} catch (e) {
  console.warn('Font registration issue:', e);
}

interface OrderCustomer {
  externalId: string;
  companyName?: string | null;
  city?: string | null;
  address?: string | null;
  postCode?: string | null;
  country?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

interface OrderCreditPackage {
  externalId: string;
  name: string;
  credits: number;
  price: number;
}

interface OrderSeller {
  companyName?: string | null;
  address?: string | null;
  city?: string | null;
  postCode?: string | null;
  country?: string | null;
}

interface Order {
  externalId: string;
  status: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  customer?: OrderCustomer;
  creditPackage?: OrderCreditPackage;
  seller?: OrderSeller;
  createdAt: string;
  updatedAt: string;
}

const formatDate = (dateString: string) =>
  format(new Date(dateString), 'dd-MM-yyyy HH:mm');

const formatCurrency = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  } catch (_e) {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

const renderCustomerName = (order: Order) => {
  const first = order.customer?.firstName?.trim() || '';
  const last = order.customer?.lastName?.trim() || '';
  const full = `${first} ${last}`.trim();
  return full || order.customer?.companyName || '—';
};

const getSellerInfo = (order: Order | null) => {
  if (!order?.seller)
    return { companyName: ' ', addresses: ' ', city: ' ', country: ' ' };
  const s = order.seller;
  const addresses = [s.address, s.postCode].filter(Boolean).join(', ') || ' ';
  return {
    companyName: s.companyName || ' ',
    addresses,
    city: s.city || ' ',
    country: s.country || ' ',
  };
};

const formatDateForInvoice = (dateString: string) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatCurrencyForTable = (amount: number) => {
  try {
    const formatted = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
    return `${formatted}€`;
  } catch (_error) {
    return `${amount.toFixed(2)}€`;
  }
};

const pdfStyles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 11,
    fontFamily: 'Roboto',
    color: '#000000',
  },
  header: {
    marginBottom: 30,
  },
  invoiceTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  dateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 30,
    fontSize: 11,
  },
  twoColumns: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 30,
  },
  leftColumn: {
    width: '48%',
  },
  rightColumn: {
    width: '48%',
  },
  addressLine: {
    marginBottom: 4,
    fontSize: 11,
  },
  addressTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
    fontSize: 12,
  },
  greeting: {
    marginBottom: 20,
    fontSize: 11,
    lineHeight: 1.5,
  },
  table: {
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#000000',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
  },
  tableHeader: {
    backgroundColor: '#f0f0f0',
    fontWeight: 'bold',
  },
  tableCell: {
    padding: 8,
    fontSize: 10,
    borderRightWidth: 1,
    borderRightColor: '#000000',
  },
  tableCellLast: {
    padding: 8,
    fontSize: 10,
  },
  tableCellPos: {
    width: '8%',
    textAlign: 'center',
  },
  tableCellDesc: {
    width: '32%',
  },
  tableCellQty: {
    width: '12%',
    textAlign: 'center',
  },
  tableCellUnit: {
    width: '12%',
    textAlign: 'center',
  },
  tableCellPrice: {
    width: '18%',
    textAlign: 'right',
  },
  tableCellTotal: {
    width: '18%',
    textAlign: 'right',
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
    fontSize: 11,
  },
  summaryLabel: {
    fontWeight: 'bold',
  },
  summaryValue: {
    fontWeight: 'bold',
  },
  footer: {
    marginTop: 40,
    fontSize: 10,
    lineHeight: 1.6,
  },
  footerLine: {
    marginBottom: 4,
  },
});

const OrderPdfDocument: React.FC<{
  order: Order;
  t: (key: string) => string;
}> = ({ order, t }) => {
  const sellerInfo = getSellerInfo(order);

  const customerAddress =
    [
      order.customer?.address,
      order.customer?.postCode,
      order.customer?.city,
      order.customer?.country,
    ]
      .filter(Boolean)
      .join(', ') || '—';

  const subtotal = order.amount;
  const grandTotal = subtotal;

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.header}>
          <Text style={pdfStyles.invoiceTitle}>
            {t('orderDetails.pdf.invoice')}
          </Text>
          <View style={pdfStyles.dateRow}>
            <Text>
              {t('orderDetails.pdf.date')}: {formatDateForInvoice(order.createdAt)}
            </Text>
            <Text>
              {t('orderDetails.pdf.invoiceNumber')}: {order.externalId}
            </Text>
          </View>
        </View>

        <View style={pdfStyles.twoColumns}>
          <View style={pdfStyles.leftColumn}>
            <Text style={pdfStyles.addressTitle}>{sellerInfo.companyName}</Text>
            <Text style={pdfStyles.addressLine}>{sellerInfo.addresses}</Text>
            <Text style={pdfStyles.addressLine}>
              {sellerInfo.city}, {sellerInfo.country}
            </Text>
          </View>
          <View style={pdfStyles.rightColumn}>
            <Text style={pdfStyles.addressLine}>
              {order.customer?.companyName || renderCustomerName(order)}
            </Text>
            <Text style={pdfStyles.addressLine}>{customerAddress}</Text>
            {order.customer?.email && (
              <Text style={pdfStyles.addressLine}>
                Email: {order.customer.email}
              </Text>
            )}
          </View>
        </View>

        <View style={pdfStyles.greeting}>
          <Text>{t('orderDetails.pdf.greeting')}</Text>
          <Text>{t('orderDetails.pdf.greetingText1')}</Text>
          <Text>{t('orderDetails.pdf.greetingText2')}</Text>
        </View>

        <View style={pdfStyles.table}>
          <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
            <Text style={[pdfStyles.tableCell, pdfStyles.tableCellPos]}>
              {t('orderDetails.pdf.table.position')}
            </Text>
            <Text style={[pdfStyles.tableCell, pdfStyles.tableCellDesc]}>
              {t('orderDetails.pdf.table.description')}
            </Text>
            <Text style={[pdfStyles.tableCell, pdfStyles.tableCellQty]}>
              {t('orderDetails.pdf.table.quantity')}
            </Text>
            <Text style={[pdfStyles.tableCell, pdfStyles.tableCellUnit]}>
              {t('orderDetails.pdf.table.unit')}
            </Text>
            <Text style={[pdfStyles.tableCell, pdfStyles.tableCellPrice]}>
              {t('orderDetails.pdf.table.unitPrice')}
            </Text>
            <Text style={[pdfStyles.tableCellLast, pdfStyles.tableCellTotal]}>
              {t('orderDetails.pdf.table.total')}
            </Text>
          </View>
          <View style={pdfStyles.tableRow}>
            <Text style={[pdfStyles.tableCell, pdfStyles.tableCellPos]}>1.</Text>
            <Text style={[pdfStyles.tableCell, pdfStyles.tableCellDesc]}>
              {order.creditPackage?.name || t('orderDetails.pdf.defaultProductName')}
            </Text>
            <Text style={[pdfStyles.tableCell, pdfStyles.tableCellQty]}>1</Text>
            <Text style={[pdfStyles.tableCell, pdfStyles.tableCellUnit]}>
              {t('orderDetails.pdf.table.piece')}
            </Text>
            <Text style={[pdfStyles.tableCell, pdfStyles.tableCellPrice]}>
              {formatCurrencyForTable(order.amount)}
            </Text>
            <Text style={[pdfStyles.tableCellLast, pdfStyles.tableCellTotal]}>
              {formatCurrencyForTable(order.amount)}
            </Text>
          </View>
        </View>

        <View>
          <View style={pdfStyles.summaryRow}>
            <Text style={pdfStyles.summaryLabel}>
              {t('orderDetails.pdf.subtotal')}
            </Text>
            <Text style={pdfStyles.summaryValue}>
              {formatCurrencyForTable(subtotal)}
            </Text>
          </View>
          <View style={pdfStyles.summaryRow}>
            <Text style={pdfStyles.summaryLabel}>
              {t('orderDetails.pdf.totalAmount')}
            </Text>
            <Text style={pdfStyles.summaryValue}>
              {formatCurrencyForTable(grandTotal)}
            </Text>
          </View>
        </View>

        <View style={pdfStyles.footer}>
          <Text style={pdfStyles.footerLine}>
            {t('orderDetails.pdf.closing')}
          </Text>
          <Text style={pdfStyles.footerLine}>{sellerInfo.companyName}</Text>
          <Text style={pdfStyles.footerLine}>{sellerInfo.addresses}</Text>
          <Text style={pdfStyles.footerLine}>
            {sellerInfo.city}, {sellerInfo.country}
          </Text>
          <Text style={[pdfStyles.footerLine, { marginTop: 12 }]}>
            {t('orderDetails.pdf.invoiceNumber')}: {order.externalId}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

const OrderHistoryPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadingArchive, setDownloadingArchive] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [createOrderModalOpen, setCreateOrderModalOpen] = useState(false);
  const [createOrderForm, setCreateOrderForm] = useState({
    customerId: '',
    credits: 10,
    amount: 100,
    currency: 'EUR',
    description: '',
  });
  const [createOrderSubmitting, setCreateOrderSubmitting] = useState(false);
  const [createOrderError, setCreateOrderError] = useState<string | null>(null);
  const [customersList, setCustomersList] = useState<
    Array<{
      id: string;
      companyName: string;
      email: string;
      firstName: string;
      lastName: string;
    }>
  >([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerSearchInput, setCustomerSearchInput] = useState('');

  useEffect(() => {
    if (!createOrderModalOpen) return;
    const load = async () => {
      setCustomersLoading(true);
      try {
        const res = await apiService.getCustomers(1, 500);
        setCustomersList(
          res.customers.map(c => ({
            id: c.id,
            companyName: c.companyName ?? '',
            email: c.email ?? '',
            firstName: c.firstName ?? '',
            lastName: c.lastName ?? '',
          }))
        );
      } catch (err) {
        console.error('Failed to load customers', err);
        setCustomersList([]);
      } finally {
        setCustomersLoading(false);
      }
    };
    load();
  }, [createOrderModalOpen]);

  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getOrders({
        page: page + 1,
        limit: rowsPerPage,
      });
      setOrders(response.orders);
      setTotal(response.total);
    } catch (err: any) {
      console.error('Failed to load orders', err);
      setError(err.message || t('orderHistory.failedToLoadOrders'));
      setOrders([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewOrder = (order: Order) => {
    navigate(`/orders/${order.externalId}`, { state: order });
  };

  const handleDownloadOrder = async (order: Order) => {
    try {
      setDownloadingId(order.externalId);
      const blob = await pdf(<OrderPdfDocument order={order} t={t} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `order-${order.externalId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to generate order PDF', err);
      setError(err.message || t('orderHistory.failedToDownloadPdf'));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadArchive = async () => {
    if (!startDate || !endDate) {
      setError(t('orderHistory.archiveDownload.selectDatesRequired'));
      return;
    }
    const from = startDate.format('YYYY-MM-DD');
    const to = endDate.format('YYYY-MM-DD');
    if (startDate.isAfter(endDate)) {
      setError(t('orderHistory.archiveDownload.selectDatesRequired'));
      return;
    }
    setError(null);
    setDownloadingArchive(true);
    try {
      const blob = await apiService.getOrdersExport({ from, to });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `orders-export-${from}-${to}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to download archive', err);
      setError(err.message || t('orderHistory.archiveDownload.failed'));
    } finally {
      setDownloadingArchive(false);
    }
  };

  const handleOpenCreateOrderModal = () => {
    setCreateOrderError(null);
    setCustomerSearchInput('');
    setCreateOrderForm({
      customerId: '',
      credits: 10,
      amount: 100,
      currency: 'EUR',
      description: '',
    });
    setCreateOrderModalOpen(true);
  };

  const handleCloseCreateOrderModal = () => {
    if (!createOrderSubmitting) {
      setCreateOrderModalOpen(false);
      setCreateOrderError(null);
      setCustomerSearchInput('');
    }
  };

  const handleCreateOrderSubmit = async () => {
    setCreateOrderError(null);
    setCreateOrderSubmitting(true);
    try {
      await apiService.createManualOrder({
        customerId: createOrderForm.customerId.trim(),
        credits: createOrderForm.credits,
        amount: createOrderForm.amount,
        currency: createOrderForm.currency.trim() || 'EUR',
        description: createOrderForm.description.trim(),
      });
      setCreateOrderModalOpen(false);
      loadOrders();
    } catch (err: any) {
      setCreateOrderError(err?.message || t('orderHistory.createOrderModal.failed'));
    } finally {
      setCreateOrderSubmitting(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Typography variant="h4" component="h1">
            {t('orderHistory.title')}
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {user?.userType === 'admin' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateOrderModal}
              >
                {t('orderHistory.createOrder')}
              </Button>
            )}
            <MobileDatePicker
              label={t('orderHistory.archiveDownload.startDate')}
              value={startDate}
              onChange={newValue => setStartDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: { xs: '100%', sm: '150px' } },
                },
              }}
            />
            <MobileDatePicker
              label={t('orderHistory.archiveDownload.endDate')}
              value={endDate}
              onChange={newValue => setEndDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: { xs: '100%', sm: '150px' } },
                },
              }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={
                downloadingArchive ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <ArchiveIcon />
                )
              }
              onClick={handleDownloadArchive}
              disabled={downloadingArchive}
            >
              {t('orderHistory.archiveDownload.button')}
            </Button>
          </Stack>
        </Stack>

        <Card sx={{ mt: 3 }}>
          <CardContent>
            {error && (
              <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography variant="h6">
                          {t('orderHistory.table.order')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6">
                          {t('orderHistory.table.date')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6">
                          {t('orderHistory.table.customer')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6">
                          {t('orderHistory.table.credits')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6">
                          {t('orderHistory.table.amount')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6">
                          {t('orderHistory.table.status')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6">
                          {t('orderHistory.table.payment')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="h6">
                          {t('orderHistory.table.action')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : orders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="body2" color="text.secondary">
                            {t('orderHistory.noOrdersFound')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      orders.map(order => (
                        <TableRow key={order.externalId} hover>
                          <TableCell>{order.externalId}</TableCell>
                          <TableCell>{formatDate(order.createdAt)}</TableCell>
                          <TableCell>{renderCustomerName(order)}</TableCell>
                          <TableCell>
                            {order.creditPackage?.credits ?? '—'}
                          </TableCell>
                          <TableCell>
                            {formatCurrency(order.amount, order.currency)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={order.status}
                              size="small"
                              color={
                                order.status === 'completed'
                                  ? 'success'
                                  : order.status === 'pending' ||
                                      order.status === 'processing'
                                    ? 'info'
                                    : order.status === 'failed' ||
                                        order.status === 'cancelled'
                                      ? 'error'
                                      : 'default'
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{order.paymentMethod || '—'}</TableCell>
                          <TableCell align="center">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="center"
                            >
                              <Tooltip title={t('orderHistory.actions.view')}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewOrder(order)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title={t('orderHistory.actions.download')}
                              >
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleDownloadOrder(order)}
                                  disabled={downloadingId === order.externalId}
                                >
                                  {downloadingId === order.externalId ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <DownloadIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>

        <Dialog open={createOrderModalOpen} onClose={handleCloseCreateOrderModal} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
            {t('orderHistory.createOrderModal.title')}
            <IconButton
              aria-label="close"
              onClick={handleCloseCreateOrderModal}
              disabled={createOrderSubmitting}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {createOrderError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {createOrderError}
              </Alert>
            )}
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Autocomplete
                fullWidth
                options={customersList}
                getOptionLabel={option => {
                  const name =
                    option.companyName && option.companyName !== '---'
                      ? option.companyName
                      : `${option.firstName} ${option.lastName}`.trim();
                  return name || option.email || '';
                }}
                value={
                  customersList.find(c => c.id === createOrderForm.customerId) ?? null
                }
                inputValue={customerSearchInput}
                onInputChange={(_e, value) => setCustomerSearchInput(value)}
                onChange={(_e, newValue) =>
                  setCreateOrderForm(prev => ({
                    ...prev,
                    customerId: newValue?.id ?? '',
                  }))
                }
                loading={customersLoading}
                filterOptions={(options, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  if (!q) return options;
                  return options.filter(
                    c =>
                      (c.companyName || '').toLowerCase().includes(q) ||
                      (c.email || '').toLowerCase().includes(q) ||
                      (c.firstName || '').toLowerCase().includes(q) ||
                      (c.lastName || '').toLowerCase().includes(q)
                  );
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('orderHistory.createOrderModal.customerId')}
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {customersLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={t('orderHistory.createOrderModal.noCustomers')}
              />
              <TextField
                label={t('orderHistory.createOrderModal.credits')}
                type="number"
                inputProps={{ min: 1 }}
                value={createOrderForm.credits}
                onChange={e =>
                  setCreateOrderForm(prev => ({
                    ...prev,
                    credits: parseInt(e.target.value, 10) || 0,
                  }))
                }
                fullWidth
                required
              />
              <TextField
                label={t('orderHistory.createOrderModal.amount')}
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={createOrderForm.amount}
                onChange={e =>
                  setCreateOrderForm(prev => ({
                    ...prev,
                    amount: parseFloat(e.target.value) || 0,
                  }))
                }
                fullWidth
                required
              />
              <TextField
                label={t('orderHistory.createOrderModal.currency')}
                value={createOrderForm.currency}
                onChange={e =>
                  setCreateOrderForm(prev => ({ ...prev, currency: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label={t('orderHistory.createOrderModal.description')}
                value={createOrderForm.description}
                onChange={e =>
                  setCreateOrderForm(prev => ({ ...prev, description: e.target.value }))
                }
                fullWidth
                multiline
                rows={3}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseCreateOrderModal} disabled={createOrderSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateOrderSubmit}
              disabled={
                createOrderSubmitting || !createOrderForm.customerId.trim()
              }
            >
              {createOrderSubmitting ? (
                <CircularProgress size={24} />
              ) : (
                t('orderHistory.createOrderModal.submit')
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default OrderHistoryPage;
